import React, { useCallback, useMemo, useState } from "react";
import { MapContainer, Marker, Popup, Polyline, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { reportsApi, usersApi } from "../../services/api.service";
import { MAP_CONFIG } from "../../config/api.config";
import { T, SectionLabel, Field, Input, Btn, Modal, SearchableSelect } from "../shared/UI";
import LeafletMapCloseSearchableSelect from "../map/LeafletMapCloseSearchableSelect";
import BasemapLayer from "../map/BasemapLayer";
import { onlyOnAgents } from "../../utils/normalizers";

function toDateString(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseCreatedAt(raw) {
  if (!raw || typeof raw !== "string") return 0;
  const m = raw.match(/^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2}) (AM|PM)$/i);
  if (!m) return 0;
  let hour = Number(m[4]);
  const min = Number(m[5]);
  const sec = Number(m[6]);
  const mer = m[7].toUpperCase();
  if (mer === "PM" && hour !== 12) hour += 12;
  if (mer === "AM" && hour === 12) hour = 0;
  return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), hour, min, sec).getTime();
}

function haversineKm(a, b) {
  if (!a || !b) return null;
  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const aa = Math.sin(dLat / 2) ** 2
    + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(aa));
}

function visitIcon(n, active) {
  return L.divIcon({
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `<div style="width:28px;height:28px;border-radius:50%;background:${active ? "#7c6af7" : "#1f2d44"};border:2px solid ${active ? "#c4bbff" : "#6f87a8"};color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;box-shadow:0 0 12px rgba(0,0,0,0.35);">${n}</div>`,
  });
}

const homeIcon = L.divIcon({
  className: "",
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  html: `<div style="width:30px;height:30px;border-radius:10px;background:#1f3d2e;border:2px solid #6af7c4;color:#6af7c4;font-size:16px;display:flex;align-items:center;justify-content:center;box-shadow:0 0 12px rgba(106,247,196,0.3);">🏠</div>`,
});

function MapFitBounds({ points, home }) {
  const map = useMap();
  React.useEffect(() => {
    const coords = [];
    if (home) coords.push([home.lat, home.lng]);
    points.forEach(p => coords.push([p.lat, p.lng]));
    if (coords.length === 0) return;
    if (coords.length === 1) {
      map.setView(coords[0], 14, { animate: true });
      return;
    }
    map.fitBounds(coords, { padding: [35, 35] });
  }, [map, points, home]);
  return null;
}

function MapFocusVisit({ visit }) {
  const map = useMap();
  React.useEffect(() => {
    if (!visit) return;
    map.flyTo([visit.lat, visit.lng], Math.max(map.getZoom(), 14), { duration: 0.4 });
  }, [map, visit]);
  return null;
}

export default function VisitsMapView() {
  const today = toDateString();
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visits, setVisits] = useState([]);
  const [home, setHome] = useState(null);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [selfieUrl, setSelfieUrl] = useState("");

  const loadAgents = useCallback(async () => {
    try {
      const res = await usersApi.agentsMeta();
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setAgents(onlyOnAgents(raw));
    } catch {
      setAgents([]);
    }
  }, []);

  React.useEffect(() => { loadAgents(); }, [loadAgents]);

  const agentOptions = useMemo(
    () => [
      { value: "", label: "Select agent" },
      ...agents.map(a => ({ value: String(a.id), label: `${a.fullName || a.name || a.mobile} · ${a.mobile || "-"}` })),
    ],
    [agents]
  );

  const fetchVisits = useCallback(async () => {
    if (!agentId) {
      setError("Please select an agent");
      return;
    }
    setLoading(true);
    setError(null);
    setVisits([]);
    setHome(null);
    setActiveIdx(-1);
    try {
      let page = 1;
      let totalPages = 1;
      const all = [];
      do {
        const res = await reportsApi.amountCollections({
          agentId,
          fromDate,
          toDate,
          page,
          limit: 200,
        });
        const raw = Array.isArray(res.data?.data) ? res.data.data : [];
        const meta = res.data?.meta || {};
        totalPages = Number(meta.totalPages || 1);
        all.push(...raw);
        page += 1;
      } while (page <= totalPages);

      const detailRes = await usersApi.agentHomeLocation(agentId);
      const detail = detailRes.data?.data || detailRes.data || null;
      const homeLat = Number(detail?.latitude);
      const homeLng = Number(detail?.longitude);
      const homeLoc = Number.isFinite(homeLat) && Number.isFinite(homeLng)
        ? {
            lat: homeLat,
            lng: homeLng,
            address: detail?.address || "",
            fullName: detail?.fullName || "",
          }
        : null;
      setHome(homeLoc);

      const ordered = [...all].sort((a, b) => parseCreatedAt(a.createdAt) - parseCreatedAt(b.createdAt));
      const mapped = ordered
        .map((r, idx) => {
          const lat = Number(r.latitude);
          const lng = Number(r.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          const homeDistanceKm = homeLoc ? haversineKm(homeLoc, { lat, lng }) : null;
          return {
            ...r,
            seq: idx + 1,
            lat,
            lng,
            homeDistanceKm,
          };
        })
        .filter(Boolean);
      setVisits(mapped);
      if (mapped.length) setActiveIdx(0);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load visit points";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [agentId, fromDate, toDate]);

  const selectedAgent = agents.find(a => String(a.id) === String(agentId));
  const path = visits.map(v => [v.lat, v.lng]);
  const firstVisit = visits[0] || null;
  const homeToFirstDistanceKm = home && firstVisit ? haversineKm(home, firstVisit) : null;
  const totalCaseDistance = visits.reduce((sum, v) => sum + Number(v.distanceKm || 0), 0);
  const avgCaseDistance = visits.length ? totalCaseDistance / visits.length : 0;
  const center = home ? [home.lat, home.lng] : MAP_CONFIG.CENTER;

  return (
    <div className="mobile-padding" style={styles.wrap}>
      <div style={styles.toolbar}>
        <SectionLabel>VISITS MAP</SectionLabel>
        <div style={styles.filters}>
          <Field label="AGENT">
            <SearchableSelect
              options={agentOptions}
              value={agentId}
              onChange={(val) => { setAgentId(String(val || "")); setError(null); }}
              placeholder="Select agent"
            />
          </Field>
          <Field label="FROM DATE">
            <Input type="date" value={fromDate} max={today} onChange={(e) => setFromDate(e.target.value)} />
          </Field>
          <Field label="TO DATE">
            <Input type="date" value={toDate} min={fromDate} max={today} onChange={(e) => setToDate(e.target.value)} />
          </Field>
          <div style={styles.actions}>
            <Btn variant="ghost" onClick={() => { setFromDate(today); setToDate(today); }}>Today</Btn>
            <Btn onClick={fetchVisits} disabled={loading}>{loading ? "Loading…" : "Show Visits"}</Btn>
          </div>
        </div>
      </div>

      <div style={styles.stats}>
        <div style={styles.stat}><span>Total Visits</span><strong>{visits.length}</strong></div>
        <div style={styles.stat}><span>Total Case Distance</span><strong>{totalCaseDistance.toFixed(2)} km</strong></div>
        <div style={styles.stat}><span>Average Case Distance</span><strong>{avgCaseDistance.toFixed(2)} km</strong></div>
        <div style={styles.stat}><span>Agent</span><strong>{selectedAgent?.fullName || selectedAgent?.name || "—"}</strong></div>
      </div>

      {error && <div style={{ ...styles.info, color: T.sos }}>{error}</div>}

      <div style={styles.body}>
        <div style={styles.mapWrap}>
          <MapContainer center={center} zoom={13} style={{ width: "100%", height: "100%" }} zoomControl={false} maxZoom={20}>
            <BasemapLayer />
            <LeafletMapCloseSearchableSelect />
            <MapFitBounds points={visits} home={home} />
            <MapFocusVisit visit={activeIdx >= 0 ? visits[activeIdx] : null} />
            {path.length > 1 && (
              <Polyline positions={path} pathOptions={{ color: T.acc, weight: 3 }} />
            )}
            {home && firstVisit && (
              <Polyline
                positions={[[home.lat, home.lng], [firstVisit.lat, firstVisit.lng]]}
                pathOptions={{ color: T.acc3, weight: 2.5, dashArray: "6 6" }}
              >
                <Tooltip sticky>
                  Home → First Visit: {homeToFirstDistanceKm != null ? homeToFirstDistanceKm.toFixed(2) : "—"} km
                </Tooltip>
              </Polyline>
            )}
            {home && (
              <Marker position={[home.lat, home.lng]} icon={homeIcon}>
                <Popup>
                  <div style={styles.popup}>
                    <div style={{ fontWeight: 700 }}>Agent Home</div>
                    <div>{home.fullName || "Agent"}</div>
                    <div>{home.address || "—"}</div>
                  </div>
                </Popup>
              </Marker>
            )}
            {visits.map((v, idx) => (
              <Marker key={v.id || `${v.seq}-${idx}`} position={[v.lat, v.lng]} icon={visitIcon(v.seq, idx === activeIdx)}>
                <Popup>
                  <div style={styles.popup}>
                    <div style={{ fontWeight: 700 }}>Visit #{v.seq}</div>
                    <div>{v.customerName || "Customer"}</div>
                    <div>Case Distance: {Number(v.distanceKm || 0).toFixed(2)} km</div>
                    <div>Home Distance: {v.homeDistanceKm != null ? v.homeDistanceKm.toFixed(2) : "—"} km</div>
                    <div>{v.createdAt || ""}</div>
                    {v.selfieImage && (
                      <div style={{ marginTop: 8 }}>
                        <Btn size="sm" variant="ghost" onClick={() => setSelfieUrl(v.selfieImage)}>
                          View Selfie
                        </Btn>
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div style={styles.list}>
          <SectionLabel>VISIT POINTS</SectionLabel>
          {visits.length === 0 && !loading && (
            <div style={styles.empty}>No visit points for selected filters.</div>
          )}
          {visits.map((v, idx) => (
            <div
              key={v.id || `${v.seq}-${idx}`}
              onClick={() => setActiveIdx(idx)}
              style={{ ...styles.card, ...(idx === activeIdx ? styles.cardActive : {}) }}
            >
              <div style={styles.cardTop}>
                <div style={styles.badge}>{v.seq}</div>
                <div>
                  <div style={styles.title}>{v.customerName || "Customer"}</div>
                  <div style={styles.sub}>{v.accountNumber || "—"} · {v.createdAt || "—"}</div>
                </div>
              </div>
              <div style={styles.metrics}>
                <span>Case: <strong>{Number(v.distanceKm || 0).toFixed(2)} km</strong></span>
                <span>From Home: <strong>{v.homeDistanceKm != null ? v.homeDistanceKm.toFixed(2) : "—"} km</strong></span>
              </div>
              <div style={styles.addr}>{v.address || "No address"}</div>
              {v.selfieImage && (
                <div style={styles.selfieWrap}>
                  <Btn size="sm" variant="ghost" onClick={() => setSelfieUrl(v.selfieImage)}>
                    Show Selfie
                  </Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Modal open={!!selfieUrl} onClose={() => setSelfieUrl("")} title="Visit Selfie" width={720}>
        {selfieUrl ? (
          <img src={selfieUrl} alt="Visit selfie" style={{ width: "100%", borderRadius: 10 }} />
        ) : null}
      </Modal>
    </div>
  );
}

const styles = {
  wrap: { flex: 1, minHeight: 0, overflow: "hidden", padding: 16, display: "flex", flexDirection: "column", gap: 12 },
  toolbar: { display: "flex", flexDirection: "column", gap: 10, position: "relative", zIndex: 5000, flexShrink: 0 },
  filters: { display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr auto", gap: 10, alignItems: "end" },
  actions: { display: "flex", gap: 8, alignItems: "center", marginBottom: 1 },
  stats: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10, position: "relative", zIndex: 5000, flexShrink: 0 },
  stat: { background: T.s2, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", fontSize: 12, color: T.muted },
  info: { fontSize: 12, padding: "4px 2px" },
  body: { flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "1.3fr 0.9fr", gap: 12, position: "relative", zIndex: 1 },
  mapWrap: { border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", background: T.s2, position: "relative", zIndex: 1 },
  list: { border: `1px solid ${T.border}`, borderRadius: 12, background: T.s2, padding: 10, overflowY: "auto" },
  empty: { fontSize: 12, color: T.muted, padding: 12 },
  card: { border: `1px solid ${T.border}`, borderRadius: 10, padding: 10, marginBottom: 8, cursor: "pointer", background: T.s1 },
  cardActive: { borderColor: T.acc, boxShadow: `0 0 0 1px ${T.acc}44 inset` },
  cardTop: { display: "flex", gap: 10, alignItems: "center" },
  badge: { width: 24, height: 24, borderRadius: "50%", background: "rgba(124,106,247,0.2)", color: T.acc, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontFamily: T.fontMono, fontSize: 12 },
  title: { fontSize: 12, color: T.text, fontWeight: 700 },
  sub: { fontSize: 10, color: T.muted, marginTop: 2 },
  metrics: { display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: T.muted },
  addr: { marginTop: 8, fontSize: 11, color: T.muted2, lineHeight: 1.4 },
  selfieWrap: { marginTop: 8 },
  popup: { minWidth: 180, fontSize: 12, lineHeight: 1.4, color: T.text },
};
